import React, { useState } from 'react'
import { observer } from 'mobx-react'
import Dialog from '@/ui/dialogs/Dialog'
import { TOSDocument } from '@/features/tos/types'
import CTA from '@/ui/CTA'
import Switch from '@/ui/form/Switch'
import { toast } from 'react-toastify'

interface TermsOfServiceDialogProps {
  dialogRef: React.RefObject<HTMLDialogElement>
  tosDocument: TOSDocument
  onAccept: () => Promise<boolean>
  onDecline: () => void
}

const TermsOfServiceDialog = ({
  dialogRef,
  tosDocument,
  onAccept,
  onDecline,
}: TermsOfServiceDialogProps) => {
  const [isAccepting, setIsAccepting] = useState(false)
  const [hasAgreed, setHasAgreed] = useState(false)

  const handleAccept = async () => {
    if (!hasAgreed) {
      toast.error('Debes aceptar los términos de servicio para continuar')
      return
    }

    setIsAccepting(true)

    try {
      const success = await onAccept()

      if (!success) {
        toast.error(
          'No se pudieron aceptar los términos de servicio. Por favor, inténtelo de nuevo.',
        )
      }
    } finally {
      setIsAccepting(false)
    }
  }

  return (
    <Dialog
      ref={dialogRef}
      title="Hemos actualizado nuestros términos de uso"
      onClose={() => {}} // No close option - user must accept or logout
      allowCloseOnEscape={false}
    >
      <div className="tos-dialog">
        <p className="tos-intro">
          Para continuar utilizando el servicio, debes aceptar los siguientes{' '}
          <a href={tosDocument.url} target="_blank" rel="noreferrer">
            términos y condiciones
          </a>
          .
        </p>

        <div className="tos-agreement">
          <Switch
            id="tos-agreement"
            label="He leído y acepto los términos y condiciones."
            checked={hasAgreed}
            onChange={setHasAgreed}
          />
        </div>
        <div className="tos-actions" slot="actions">
          <CTA variant="destructive" size="medium" onClick={onDecline}>
            Cerrar sesión
          </CTA>

          <CTA
            variant="system"
            size="medium"
            onClick={handleAccept}
            isLoading={isAccepting}
            isDisabled={isAccepting || !hasAgreed}
          >
            {isAccepting ? 'Aceptando...' : 'Aceptar términos'}
          </CTA>
        </div>
      </div>
    </Dialog>
  )
}

export default observer(TermsOfServiceDialog)
